import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Leaf, ShieldCheck, Store, User } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const demos = [
  { role: "Super Admin", icon: ShieldCheck, email: "aca.anshul28@gmail.com", password: "Admin@123", color: "emerald" },
  { role: "Franchise Owner", icon: Store, email: "owner@dksupermart.com", password: "Owner@123", color: "orange" },
  { role: "Customer", icon: User, email: "customer@dksupermart.com", password: "Customer@123", color: "sky" },
];

export default function Login() {
  const { user, login, register } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) navigate(["super_admin", "head_office"].includes(user.role) ? "/portal/admin" : user.role === "customer" ? "/" : "/portal/store");
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const res = mode === "login" ? await login(email, password) : await register({ name, email, password });
    setLoading(false);
    if (res.ok) {
      toast.success("Welcome!");
      const r = res.user.role;
      navigate(["super_admin", "head_office"].includes(r) ? "/portal/admin" : r === "customer" ? "/" : "/portal/store");
    } else toast.error(res.error);
  };

  const fill = (d) => { setMode("login"); setEmail(d.email); setPassword(d.password); };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:block relative bg-slate-900">
        <img src="https://images.unsplash.com/photo-1604719312566-8912e9227c6a?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000" alt="Store" className="absolute inset-0 w-full h-full object-cover opacity-40" />
        <div className="relative p-12 h-full flex flex-col justify-between">
          <Link to="/" className="flex items-center gap-2 text-white">
            <span className="grid place-items-center w-9 h-9 rounded-xl bg-yellow-400"><Leaf className="w-5 h-5" /></span>
            <span className="font-heading font-extrabold text-lg">D K BAZAAR</span>
          </Link>
          <div className="text-white">
            <h2 className="font-heading text-3xl font-bold">Your network. One dashboard.</h2>
            <p className="text-slate-300 mt-2 max-w-sm">Manage stores, franchises, inventory and orders from a single trusted portal.</p>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-sm">
          <Link to="/" className="lg:hidden flex items-center gap-2 mb-8">
            <span className="grid place-items-center w-9 h-9 rounded-xl bg-yellow-400 text-slate-900"><Leaf className="w-5 h-5" /></span>
            <span className="font-heading font-extrabold text-lg">D K BAZAAR</span>
          </Link>
          <h1 className="font-heading text-2xl font-bold">{mode === "login" ? "Welcome back" : "Create account"}</h1>
          <p className="text-muted-foreground text-sm mt-1">{mode === "login" ? "Sign in to your portal or account." : "Register as a customer to shop faster."}</p>

          <Tabs value={mode} onValueChange={setMode} className="mt-6">
            <TabsList className="grid grid-cols-2 w-full">
              <TabsTrigger value="login" data-testid="tab-login">Sign In</TabsTrigger>
              <TabsTrigger value="register" data-testid="tab-register">Register</TabsTrigger>
            </TabsList>
          </Tabs>

          <form onSubmit={submit} className="mt-6 space-y-4">
            {mode === "register" && <div><Label>Name</Label><Input data-testid="login-name" value={name} onChange={(e) => setName(e.target.value)} required /></div>}
            <div><Label>Email</Label><Input data-testid="login-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required /></div>
            <div><Label>Password</Label><Input data-testid="login-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required /></div>
            <Button type="submit" disabled={loading} data-testid="login-submit" className="w-full bg-yellow-400 hover:bg-yellow-500 text-slate-900 h-11">{loading ? "Please wait…" : mode === "login" ? "Sign In" : "Create Account"}</Button>
          </form>

          {mode === "login" && (
            <div className="mt-8">
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground text-center">Quick demo accounts</p>
              <div className="mt-3 space-y-2">
                {demos.map((d) => (
                  <button key={d.role} data-testid={`demo-${d.role.replace(/\s/g, "-").toLowerCase()}`} onClick={() => fill(d)}
                    className="w-full flex items-center gap-3 rounded-xl border border-slate-200 dark:border-slate-800 p-3 hover:border-yellow-400 hover:bg-yellow-100/50 dark:hover:bg-slate-800 transition-all text-left">
                    <span className="grid place-items-center w-9 h-9 rounded-lg bg-yellow-100 dark:bg-yellow-950 text-yellow-600"><d.icon className="w-4 h-4" /></span>
                    <div className="flex-1"><p className="text-sm font-semibold">{d.role}</p><p className="text-xs text-muted-foreground">{d.email}</p></div>
                    <span className="text-xs text-yellow-700 font-medium">Fill →</span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
